import React, { FC, memo } from 'react';
import Button from '@mui/material/Button';
import DialogActions from '@mui/material/DialogActions';
import makeStyles from '@mui/styles/makeStyles';

const useStyles = makeStyles((theme) => ({
    actions: {
        borderTop: `1px solid ${theme.palette.grey[200]}`,
        boxShadow: '0 -2px 8px rgba(0,0,0,0.04)',
        padding: theme.spacing(1.5, 3),
        gap: theme.spacing(1)
    }
}));

type Props = {
    rowData: any;
    setDefaultValues: Function | null;
    setChangeRate: Function | any;
    setRate: Function;
    setAmount?: (amount: number) => void;
};

const TableActionButtons: FC<Props> = ({ rowData, setDefaultValues, setChangeRate, setRate, setAmount }) => {
    const classes = useStyles();

    const handleOk = () => {
        const primaryRow = rowData.find((r: any) => r.isPrimaryCurrency);
        const documentCurrencyRow = rowData.find((r: any) => r.isDocumentCurrency);
        const localRow = rowData.find((r: any) => r.isLocalCurrency);

        // The stored rate is the DOCUMENT-currency → local rate (that's what
        // callers persist as `rate`/`supplierRate` and use to convert). Take it
        // from the document-currency row — NOT the primary row, which only
        // coincides when the document currency IS the primary currency. For a
        // local-currency document this correctly yields 1; for e.g. EUR it yields
        // the EUR→local rate instead of the USD→local rate.
        const rateRow = documentCurrencyRow || primaryRow;
        // Derive the stored rate from the AMOUNTS at full precision (local ÷ doc)
        // rather than the 4-decimal display rate. For a small rate like JPY→ILS
        // (~0.0185) the 4dp rounding shifts the USD equivalent by ~0.5%, so the
        // amount the user typed wouldn't round-trip. local ÷ doc reproduces it
        // exactly (the local amount is already the pivot).
        let rateValue: number | undefined;
        if (rateRow?.isLocalCurrency) {
            // A LOCAL-currency document stores the local↔PRIMARY (₪↔$) rate, not
            // 1. Its doc→local rate is trivially 1 and carries no information, so
            // RateControlBox shows the ₪→$ rate instead and every builder derives
            // the second column as `amount / rate` for a local doc. Writing 1 here
            // threw away the rate the user had just typed in the $ row — set ILS,
            // Change Rate, enter 3, and the field came back 1 — and made the USD
            // column on the PDF equal the shekel amount.
            const primaryRate =
                typeof primaryRow?.rate === 'object' ? primaryRow.rate.exchangeRateSell : primaryRow?.rate;
            rateValue = Number(primaryRate) > 0 ? Number(primaryRate) : 1;
        } else if (localRow && Number(rateRow?.amount) > 0) {
            // Derive the rate from the 2-decimal doc amount that will actually be
            // STORED (setAmount rounds to 2dp). Using the full-precision amount
            // here left stored amount*rate a cent short (e.g. a 100 ILS pivot
            // rendered as 99.99). Rounding the denominator to match the stored
            // amount keeps the local total exact through the PDF and reports.
            const storedDocAmount = Math.round(Number(rateRow.amount) * 100) / 100;
            rateValue =
                storedDocAmount > 0
                    ? Number(localRow.amount) / storedDocAmount
                    : Number(localRow.amount) / Number(rateRow.amount);
        } else {
            rateValue = typeof rateRow?.rate === 'object' ? rateRow.rate.exchangeRateSell : rateRow?.rate;
        }
        if (rateValue != null) {
            setRate(+rateValue);
        }

        // Amount is from the document currency row (the currency the receipt is in)
        const amountRow = documentCurrencyRow || localRow || primaryRow;
        if (setAmount && amountRow?.amount != null) {
            setTimeout(() => setAmount(+amountRow.amount), 0);
        }

        setChangeRate(false);
    };

    return (
        <DialogActions className={classes.actions}>
            {setDefaultValues && (
                <Button variant="outlined" onClick={() => setDefaultValues()}>
                    Reset
                </Button>
            )}
            <Button variant="outlined" onClick={() => setChangeRate(false)}>
                Cancel
            </Button>
            <Button variant="contained" color="primary" onClick={handleOk}>
                OK
            </Button>
        </DialogActions>
    );
};

export default memo(TableActionButtons);
